/**
 * はがき郵送に関する共通ロジック。
 * - 学校画面・管理画面の両方から利用できるように @common/utils 配下に定義する。
 */

import { parseAddTownCompanyRequestStatus } from './add-town-company-request-status'

/** はがき郵送を行うための最低郵送件数 */
export const POSTCARD_MIN_SEND_COUNT = 50

export type RequestForMinSendCount = {
  requestedCount: number
  status: string
  sendRequestNumber?: string | null
}

/**
 * 郵送回未設定かつキャンセル以外のリクエストの requested_count 合計を返す。
 * 画面表示・最低件数判定で共通利用する。
 */
export const sumTotalRequestedWithoutSendNumber = (
  requests: RequestForMinSendCount[],
): number => {
  return requests
    .filter((request) => {
      if (request.sendRequestNumber) return false
      return parseAddTownCompanyRequestStatus(request.status) !== 'cancelled'
    })
    .reduce((sum, request) => sum + request.requestedCount, 0)
}

/**
 * はがき郵送を行うのに十分な件数かどうかを判定する。
 * @param totalRequestedWithoutSendNumber 郵送回未設定のリクエストの requested_count 合計
 * @param count 郵送対象（はがき郵送）の件数
 * @returns 最低件数以上であれば true
 */
export const hasEnoughPostcardRecipients = (
  totalRequestedWithoutSendNumber: number,
  count: number,
): boolean => {
  return count >= getPostcardMinSendCount(totalRequestedWithoutSendNumber)
}

/**
 * はがき郵送に必要な最低件数を返す。
 * 50件 または データ生成リクエスト件数 のいずれか大きい方。
 */
export const getPostcardMinSendCount = (totalRequestedWithoutSendNumber: number): number => {
  return Math.max(POSTCARD_MIN_SEND_COUNT, totalRequestedWithoutSendNumber)
}

/** 郵送開始時に必須とする事業所フィールド */
export type ShippingRecipientRequiredFields = {
  managementId?: string | null
  companyName?: string | null
  postalCode?: string | null
  address?: string | null
}

export const isShippingRecipientMissingRequiredFields = (
  recipient: ShippingRecipientRequiredFields,
): boolean => {
  return (
    !recipient.companyName?.trim() ||
    !recipient.postalCode?.trim() ||
    !recipient.address?.trim()
  )
}

export const findShippingRecipientsMissingRequiredFields = <T extends ShippingRecipientRequiredFields>(
  recipients: T[],
): T[] => {
  return recipients.filter(isShippingRecipientMissingRequiredFields)
}

export const buildShippingRecipientsMissingRequiredFieldsMessage = (
  missing: ShippingRecipientRequiredFields[],
): string => {
  const ids = missing
    .map((recipient) => recipient.managementId?.trim())
    .filter((id): id is string => Boolean(id))
  const idPart =
    ids.length === 0
      ? ''
      : ids.length <= 10
        ? `（管理ID: ${ids.join(', ')}）`
        : `（管理ID: ${ids.slice(0, 10).join(', ')} ほか${ids.length - 10}件）`
  return `郵送対象に事業所名・郵便番号・住所が未入力の事業所があります。入力してから郵送開始してください。${idPart}`
}

/** 未入力があればエラー文言、なければ null */
export const getShippingRecipientsRequiredFieldsError = (
  recipients: ShippingRecipientRequiredFields[],
): string | null => {
  const missing = findShippingRecipientsMissingRequiredFields(recipients)
  if (missing.length === 0) return null
  return buildShippingRecipientsMissingRequiredFieldsMessage(missing)
}

